import { useMemo, useState } from 'react';

import { contextChips } from '../lib/ai-context.js';
import { usePlay } from '../state/play.js';
import { useModel } from '../state/model.js';
import { useScene } from '../state/scene.js';
import { useSound } from '../state/sound.js';
import { useWorkspace } from '../state/workspace.js';
import { Chat } from './Chat.js';
import { GenerationInbox } from './generation/GenerationInbox.js';
import { useGenerationFeed } from './generation/hooks.js';
import { LocalGenButton, LocalGenSettings, WaitingBadge } from './generation/LocalGenPopover.js';
import { IconButton } from './ui/controls.js';
import { Icon } from './ui/icons.js';
import { WEBSITE, LOCAL_AI } from '../lib/edition.js';
import { requireLocalGen } from '../state/local-gen.js';

/** Editor context for the AI, derived from what is open and selected. */
export function useEditorContextChips() {
  const view = useWorkspace((s) => s.view);
  const activeFile = useWorkspace((s) => s.tabs.at(-1) ?? null);
  const scenePath = useScene((s) => s.path);
  const sceneDoc = useScene((s) => s.doc);
  const selection = useScene((s) => s.selection);
  const modelPath = useModel((s) => s.path);
  const selectedPart = useModel((s) => s.selected);
  const playState = usePlay((s) => s.state);
  // Read loosely so the Sound store can grow (doc, selected, analysis) without
  // this panel depending on its exact shape.
  const soundPath = useSound((s) => s.path);
  const soundDoc = useSound((s) => (s as { doc?: unknown }).doc ?? null);
  const soundSelection = useSound((s) => {
    const selected = (s as { selected?: unknown }).selected;
    return typeof selected === 'string' ? selected : null;
  });
  const soundAnalysis = useSound((s) => (s as { analysis?: unknown }).analysis ?? null);

  return useMemo(() => {
    const selected = selection
      .map((id) => sceneDoc?.entities.find((e) => e.id === id))
      .filter((e): e is NonNullable<typeof e> => Boolean(e))
      .map((e) => ({ id: e.id, name: e.name ?? null }));
    return contextChips({
      tab: view,
      scenePath,
      selectedEntities: selected,
      modelPath,
      selectedPart,
      filePath: activeFile,
      playing: playState === 'playing' || playState === 'paused',
      soundPath,
      soundDoc,
      soundSelection,
      soundAnalysis,
    });
  }, [
    view,
    activeFile,
    scenePath,
    sceneDoc,
    selection,
    modelPath,
    selectedPart,
    playState,
    soundPath,
    soundDoc,
    soundSelection,
    soundAnalysis,
  ]);
}

/** The persistent AI dock on the right of Scene/Model/Play/Code. */
export function AiPanel() {
  const setAiOpen = useWorkspace((s) => s.setAiOpen);
  const chips = useEditorContextChips();
  const [attach, setAttach] = useState(true);
  const [tab, setTab] = useState<'chat' | 'generations'>('chat');
  // Poll the generation queue while the assistant window is on screen (feeds the badges).
  useGenerationFeed(true);

  return (
    <aside className="ai-panel" aria-label="AI assistant">
      <header className="dock-head">
        <span className="dock-title">
          <Icon name="sparkles" size={14} className="ai-glyph" />
          AI
        </span>
        <span className="dock-head-spacer" />
        {LOCAL_AI && <LocalGenButton active={tab === 'generations'} onOpen={() => requireLocalGen() && setTab('generations')} />}
        <IconButton icon="x" title="Hide AI panel" shortcut="Ctrl+J" onClick={() => setAiOpen(false)} />
      </header>
      <div className="subtabs panel-subtabs" role="tablist" aria-label="Assistant views">
        <button type="button" role="tab" aria-selected={tab === 'chat'} className={`subtab${tab === 'chat' ? ' is-active' : ''}`} onClick={() => setTab('chat')}>
          Chat
        </button>
        {LOCAL_AI && (
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'generations'}
          className={`subtab${tab === 'generations' ? ' is-active' : ''}`}
          onClick={() => requireLocalGen() && setTab('generations')}
          title="Local generations: settings, approvals, results and the queue"
        >
          Generations
          <WaitingBadge />
        </button>
        )}
      </div>
      {/* The chat stays mounted so a half-typed message survives a visit to Generations. */}
      <div style={{ display: tab === 'chat' ? 'contents' : 'none' }}>
        <Chat chips={chips} attachContext={attach} onToggleContext={() => setAttach((v) => !v)} dock />
      </div>
      {tab === 'generations' && (
        <div className="gen-pane">
          <LocalGenSettings />
          <GenerationInbox active />
        </div>
      )}
    </aside>
  );
}
